'use client';

import Image from 'next/image';
import { PropsWithChildren, useCallback } from 'react';

import { CommentSection } from '@/components/comment-section';
import { CreateComment } from '@/components/create-comment';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { getCommentsScopeKey } from '@/features/comments/utils/get-comments-scope-key.util';
import { useCommentsStore } from '@/store/comments.store';

export interface SelectedParagraph {
  paragraphId: string;
  text: string;
  index: number;
}

export interface CommentDrawerProps {
  chapterTitle: string;
  currentChapterIndex: number;
  totalChapters: number;
  selectedParagraph: SelectedParagraph | null;
  fanficId: string;
  chapterId: string;
  postId?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onParagraphSelect?: (paragraph: SelectedParagraph | null) => void;
  onCommentsChange?: () => void;
}

export const CommentDrawer = ({
  chapterTitle,
  currentChapterIndex,
  totalChapters,
  selectedParagraph,
  fanficId,
  chapterId,
  postId,
  open,
  onOpenChange,
  children,
  onCommentsChange,
}: PropsWithChildren<CommentDrawerProps>) => {
  const resolvedPostId = postId ?? (fanficId ? Number(fanficId) : undefined);
  const addComment = useCommentsStore((state) => state.addComment);
  const scopeKey = getCommentsScopeKey({
    postId: resolvedPostId,
    chapterId,
    paragraphId: selectedParagraph?.paragraphId,
  });

  const handleCreated = useCallback(
    (newComment: unknown) => {
      addComment(scopeKey, newComment as Parameters<typeof addComment>[1]);
      onCommentsChange?.();
    },
    [addComment, onCommentsChange, scopeKey],
  );

  return (
    <Drawer
      direction="right"
      open={open}
      onOpenChange={onOpenChange}
      shouldScaleBackground={false}
    >
      {children && <DrawerTrigger asChild>{children}</DrawerTrigger>}
      <DrawerContent className="left-auto inset-y-0 mt-0 w-full md:w-[499px] overflow-hidden rounded-none bg-white flex flex-col">
        <DrawerHeader className="sr-only">
          <DrawerTitle>Комментарии к абзацу</DrawerTitle>
        </DrawerHeader>
        <div className="px-3 pt-4 pb-3 flex-shrink-0 bg-[#F7F7F7]">
          <p className="font-black leading-[115%] uppercase text-[#1B1D22]">{chapterTitle}</p>
          <p className="text-[#898989] text-xs leading-[140%] font-medium mt-1">
            Глава {currentChapterIndex + 1} из {totalChapters}
          </p>
        </div>
        <Separator />
        <div className="flex-1 overflow-y-auto flex flex-col">
          {selectedParagraph && resolvedPostId ? (
            <>
              <div className="bg-[#F7F7F7]">
                <div className="p-3">
                  <p className="text-xs text-[#1B1D22] italic leading-relaxed">
                    {selectedParagraph.text}
                  </p>
                </div>
              </div>
              <div className="px-3 pt-3 pb-4 flex flex-col gap-4">
                <CreateComment
                  postId={resolvedPostId}
                  chapterId={chapterId}
                  paragraphId={selectedParagraph.paragraphId}
                  onSubmit={handleCreated}
                />
                <CommentSection
                  postId={resolvedPostId}
                  chapterId={chapterId}
                  paragraphId={selectedParagraph.paragraphId}
                  hideTitle
                  onCommentsChange={onCommentsChange}
                />
              </div>
            </>
          ) : (
            <div className="px-3 pt-3 pb-4">
              <div className="mt-[60px] flex flex-col items-center gap-4">
                <Image
                  src="/images/nocomments.png"
                  width={100}
                  height={100}
                  alt="Иконка отсутствия комментариев"
                />
                <p className="font-black text-lg tracking-tight text-center text-[#1B1D22]">
                  ВЫБЕРИТЕ АБЗАЦ
                </p>
                <p className="text-xs text-[#898989] text-center">
                  Нажмите на иконку комментариев у абзаца, чтобы открыть обсуждение
                </p>
              </div>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
